import { useState } from "react";
import Sidebar from "../components/Sidebar";

// SVG Icon Components
const LinkIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
 strokeLinejoin="round"
      strokeWidth={2}
      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
    />
  </svg>
);

const UsersIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
    />
  </svg>
);

const TrendIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
 strokeLinejoin="round"
      strokeWidth={2}
      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
    />
  </svg>
);

const TierIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
 strokeLinejoin="round"
      strokeWidth={2}
      d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
    />
  </svg>
);

interface AffiliateTier {
  name: string;
  commission: number;
  color: string;
  minReferrals: number;
  maxReferrals: number;
  icon: string;
}

interface AffiliateStats {
  currentTier: string;
  referralsThisMonth: number;
  totalReferrals: number;
  totalEarnings: number;
  pendingEarnings: number;
}

interface Referral {
  id: string;
  referredUser: string;
  date: Date;
  status: "active" | "inactive" | "pending";
  earnings: number;
}

export default function Affiliate() {
  const stats: AffiliateStats = {
    currentTier: "Bronze",
    referralsThisMonth: 7,
    totalReferrals: 45,
    totalEarnings: 1250,
    pendingEarnings: 320
  };
  const affiliateLink = "https://aegd.com/ref/USER123";
  const [copied, setCopied] = useState(false);
  const referrals: Referral[] = [
    { id: "REF-001", referredUser: "john@example.com", date: new Date(), status: "active", earnings: 45 },
    { id: "REF-002", referredUser: "jane@example.com", date: new Date(Date.now() - 86400000), status: "active", earnings: 32 },
    { id: "REF-003", referredUser: "mike@example.com", date: new Date(Date.now() - 172800000), status: "pending", earnings: 0 },
  ];

  const tiers: AffiliateTier[] = [
    { name: "Bronze", commission: 1, color: "#CD7F32", minReferrals: 0, maxReferrals: 10, icon: "🥉" },
    { name: "Silver", commission: 1.5, color: "#C0C0C0", minReferrals: 11, maxReferrals: 50, icon: "🥈" },
    { name: "Gold", commission: 2, color: "#FFD700", minReferrals: 51, maxReferrals: 100, icon: "🥇" },
    { name: "Platinum", commission: 2.5, color: "#E5E4E2", minReferrals: 101, maxReferrals: Infinity, icon: "💎" },
  ];

  const currentTierData = tiers.find(t => t.name === stats.currentTier);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(affiliateLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getProgressToNextTier = () => {
    const currentTierIndex = tiers.findIndex(t => t.name === stats.currentTier);
    if (currentTierIndex < tiers.length - 1) {
      const nextTier = tiers[currentTierIndex + 1];
      const progress = ((stats.referralsThisMonth - currentTierData?.minReferrals!) / (nextTier.minReferrals - currentTierData?.minReferrals!)) * 100;
      return Math.min(Math.max(progress, 0), 100);
    }
    return 100;
  };

  return (
    <div className="flex min-h-screen bg-white">
      <Sidebar />
      <div className="flex-1">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Affiliate Program</h1>
              <p className="text-sm text-gray-500">Earn commissions by referring new customers</p>
            </div>
          </div>
        </header>

        {/* Affiliate Content */}
        <div className="p-6">
          {/* Current Tier Card */}
          <div className="card bg-white border border-gray-200 rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium mb-1">Current Tier</p>
                <div className="flex items-center gap-3">
                  <span className="text-5xl">{currentTierData?.icon}</span>
                  <div>
                    <p className="text-4xl font-bold text-gray-900">{stats.currentTier}</p>
                    <p className="text-lg text-cyan-600">{currentTierData?.commission}% Commission</p>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600 font-medium">This Month</p>
                <p className="text-3xl font-bold text-gray-900">{stats.referralsThisMonth}</p>
                <p className="text-sm text-gray-500">Referrals</p>
              </div>
            </div>
            <div className="mt-6">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Progress to {tiers[tiers.findIndex(t => t.name === stats.currentTier) + 1]?.name || "Max Tier"}</span>
                <span>{getProgressToNextTier().toFixed(0)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-cyan-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${getProgressToNextTier()}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Affiliate Link */}
            <div className="card bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <LinkIcon />
                Your Affiliate Link
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Referral Link
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={affiliateLink}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-xl bg-gray-50 focus:outline-none"
                    />
                    <button
                      onClick={copyToClipboard}
                      className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                    >
                      {copied ? "Copied!" : "Copy"}
                    </button>
                  </div>
                </div>

                <p className="text-xs text-gray-500">
                  Share this link to earn {currentTierData?.commission}% commission on every successful referral
                </p>
              </div>
            </div>

            {/* Earnings Overview */}
            <div className="card bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <TrendIcon />
                Earnings Overview
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl">
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Total Earnings</p>
                    <p className="text-2xl font-bold text-gray-900">฿{stats.totalEarnings.toLocaleString()}</p>
                  </div>
                  <span className="text-green-500">+12.5%</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl">
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Pending Earnings</p>
                    <p className="text-2xl font-bold text-gray-900">฿{stats.pendingEarnings.toLocaleString()}</p>
                  </div>
                  <span className="text-yellow-500">Awaiting</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tier Structure */}
          <div className="card bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow mt-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TierIcon />
              Tier Structure
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {tiers.map((tier) => (
                <div
                  key={tier.name}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    tier.name === stats.currentTier
                      ? "border-blue-500 bg-gray-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="text-center">
                    <div className="text-3xl mb-2">{tier.icon}</div>
                    <p className="font-bold text-gray-900">{tier.name}</p>
                    <p className="text-lg font-bold" style={{ color: tier.color }}>{tier.commission}%</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {tier.minReferrals}-{tier.maxReferrals === Infinity ? "+" : tier.maxReferrals} refs
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Referral History */}
          <div className="card bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow mt-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <UsersIcon />
              Referral History
            </h3>
            
            <div className="space-y-3">
              {referrals.map((referral) => (
                <div
                  key={referral.id}
                  className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        referral.status === "active"
                          ? "bg-green-500 text-white"
                          : referral.status === "pending"
                            ? "bg-yellow-500 text-white"
                            : "bg-gray-500 text-white"
                      }`}
                    >
                      <UsersIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {referral.referredUser}
                      </p>
                      <p className="text-xs text-gray-500">{referral.id}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p
                      className={`text-lg font-bold ${
                        referral.earnings > 0
                          ? "text-green-600"
                          : "text-gray-400"
                      }`}
                    >
                      ฿{referral.earnings}
                    </p>
                    <p className="text-xs text-gray-500">
                      {referral.date.toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
